import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	CUSTOM_ELEMENTS_SCHEMA,
	DestroyRef,
	effect,
	EventEmitter,
	Inject,
	input,
	Optional,
	Output,
	signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PanelModule } from '@go-modules/components/panel/panel.module';
import { NgxToolbarModule } from '@go-modules/components/toolbar/toolbar.module';
import { NgxToolbarButtonModule } from '@go-modules/components/toolbar/toolbar-button/toolbar-button.module';
import { Action } from '@go-modules/components/toolbar/toolbar-button/action-to-icon-class-map';
import { NgxMediaThumbnailModule } from '@go-modules/components/media-thumbnail/media-thumbnail.module';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { mediaPreviewToken } from 'go-modules/modals/media-preview/modal.factory';
import { SanitizePipeModule } from '@go-modules/pipes/sanitize/sanitize.pipe.module';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { InstructionsPreviewDialogComponent } from '@go-modules/components/dialogs/instructions-preview/instructions-preview-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogData } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { TaskInstructionsDialogComponent } from '@go-modules/components/dialogs/task-instructions-dialog/task-instructions-dialog.component';
import type { TaskInstructionsDialogData } from '@go-modules/components/dialogs/task-instructions-dialog/task-instructions-dialog.component';
import {
	LibraryDialogComponent,
	type LibraryDialogContext
} from '@go-modules/components/dialogs/library-dialog/library-dialog.component';
import { filter, tap } from 'rxjs';
import { ActivityEditModeService } from '@go-modules/services/activity-edit-mode/activity-edit-mode.service';
import {
	ACTIVITY_EDITOR_CONTEXT,
	ActivityEditorContext
} from '@go-modules/components/dialogs/activity-preview-editor/activity-editor-context';
import { NgxRichTextEditorModule } from '@go-modules/components/rich-text-editor';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { EventService } from '@go-modules/services/event/event.service';
import type { GoEvent } from '@go-modules/services/event/event.service';
import {
	CONTENT_TYPES,
	MODES
} from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import type { MediaFactory, MediaSchema } from 'go-modules/models/media';
import { TaskType, type Task } from '@go-modules/interfaces/task';
import {
	ActivityPreviewInlineTaskInstructionStateService
} from '@go-modules/components/dialogs/activity-preview-editor/activity-preview-inline-task-instruction-state.service';

interface GroupContext {
	group_id?: number | null;
}

type InlineInstructionTaskKey = number | TaskType;

interface TaskInstructionUpdate {
	text: string;
	media: MediaSchema | null;
}

@Component({
	selector: 'task-instruction',
	standalone: true,
	imports: [
		TranslateModule,
		PanelModule,
		NgxToolbarModule,
		NgxToolbarButtonModule,
		NgxMediaThumbnailModule,
		SanitizePipeModule,
		NgxRichTextEditorModule
	],
	templateUrl: './task-instruction.component.html',
	styleUrls: ['./task-instruction.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class TaskInstructionComponent {
	@Output() public onEdit = new EventEmitter<TaskInstructionUpdate>();
	@Output() public onRemove = new EventEmitter<void>();

	public taskId = input.required<number>();
	public inlineEditor = input(false);
	public startEditing = input(false);
	public toolbarActions = Action;
	public readonly CHARACTER_LIMIT = 20000;
	private inlineEditorMounted = signal(true);
	private selectedInlineEditorTaskKey: InlineInstructionTaskKey | null = null;
	private inlineEditorRemountTimeout: ReturnType<typeof setTimeout> | null = null;
	private modeledMediaCache = new Map<MediaSchema['media_id'] | MediaSchema, MediaSchema>();

	private task = computed(() => {
		if (this.editorContext != null) {
			return this.editorContext.selectedTask() ?? undefined;
		}

		const taskId = this.taskId();
		return this.activityEditModeService.tasksById().get(taskId);
	});
	public customInstructionsHtml = computed(() => this.task()?.instruction?.text ?? '');
	public editing = computed(() => {
		return this.inlineTaskInstructionState?.isEditingTaskInstruction(this.task()) ?? false;
	});
	public editorInstructionsHtml = computed(() => {
		return this.inlineTaskInstructionState?.getInstructionDraftText(
			this.task(),
			this.customInstructionsHtml()
		) ?? this.customInstructionsHtml();
	});
	public renderedMedia = computed<MediaSchema | null>(() => {
		const media = this.media();
		if (media == null) {
			return null;
		}

		const mediaCacheKey = media.media_id == null ? media : media.media_id;
		const modeledMediaCache = this.modeledMediaCache.get(mediaCacheKey);
		if (modeledMediaCache != null) {
			return modeledMediaCache;
		}

		const modeledMedia = this.MediaModel.model(media);
		this.modeledMediaCache.set(mediaCacheKey, modeledMedia);
		return modeledMedia;
	});
	public shouldRenderInlineEditor = computed(() =>
		this.inlineEditorMounted() && this.getInlineInstructionTaskKey(this.task()) != null
	);
	public media = computed<MediaSchema | null>(() => this.task()?.instruction?.media ?? null);
	public showInlineEditor = computed(() => this.inlineEditor() && (this.startEditing() || this.editing()));

	constructor (
		private ngxGoModalService: GoModalService,
		private activityEditModeService: ActivityEditModeService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef,
		private eventService: EventService,
		private destroyRef: DestroyRef,
		@Inject(mediaToken) public MediaModel: MediaFactory,
		@Inject(mediaPreviewToken) private mediaPreviewModal,
		@Optional() @Inject(ACTIVITY_EDITOR_CONTEXT) private editorContext: ActivityEditorContext | null,
		@Optional() private inlineTaskInstructionState: ActivityPreviewInlineTaskInstructionStateService | null
	) {
		this.destroyRef.onDestroy(() => this.clearInlineEditorRemountTimeout());

		this.eventService.listen(EVENT_NAMES.MEDIA_SYNC)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((event: GoEvent) => this.syncInstructionMedia(event.data));

		effect(() => {
			const taskKey = this.getInlineInstructionTaskKey(this.task());
			const previousTaskKey = this.selectedInlineEditorTaskKey;

			if (taskKey === previousTaskKey) {
				return;
			}

			this.selectedInlineEditorTaskKey = taskKey;
			if (previousTaskKey != null && taskKey != null) {
				this.remountInlineEditor();
			}
		});

	}

	public preview () {
		this.ngxGoModalService.open(InstructionsPreviewDialogComponent, false, {
			data: {
				instruction: {
					text: this.customInstructionsHtml(),
					media: this.media()
				}
			}
		});
	}

	public edit () {
		if (this.inlineEditor()) {
			this.inlineTaskInstructionState?.startEditingTaskInstruction(this.task(), this.customInstructionsHtml());
			return;
		}

		return this.ngxGoModalService.open(TaskInstructionsDialogComponent, true, {
			data: {
				text: this.customInstructionsHtml(),
				media: this.media(),
				...this.getTaskInstructionsDialogContextData()
			} as TaskInstructionsDialogData
		}).afterClosed().subscribe((result) => {
			if (result == null) {
				return;
			}

			const hasMedia = result.media != null && Object.keys(result.media).length > 0;
			const currentInstruction = this.task()?.instruction ?? {};

			this.onEdit.emit({
				...currentInstruction,
				text: result.text,
				media: hasMedia ? this.MediaModel.model(result.media) : null
			});
		});
	}

	public remove () {
		const dialogRef: MatDialogRef<ConfirmDialogComponent, boolean> = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('task-details_instructions_remove_message_title'),
				message: this.translate.instant('task-details_instructions_remove_message_body'),
				confirmText: this.translate.instant('common_remove'),
				cancelText: this.translate.instant('common_cancel')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed().pipe(
			filter((confirm) => confirm),
			tap(() => this.onRemove.emit())
		).subscribe();
	}

	public handleEditorTextUpdate (updatedText: string): void {
		this.setInstructionDraft(updatedText);
		if (this.inlineEditor()) {
			this.emitInstructionUpdate(updatedText, this.renderedMedia());
		}
	}

	public handleEditorFocusOut (updatedText: string): void {
		this.setInstructionDraft(updatedText);
		this.emitInstructionUpdate(updatedText, this.renderedMedia());
	}

	public instructionTextLength (): number {
		return this.editorInstructionsHtml().length;
	}

	public openMediaPreviewModal (edit: boolean): void {
		const modalData = {
			media: this.renderedMedia(),
			edit,
			allowDownload: true,
			groupId: this.resolveGroupId()
		};

		this.mediaPreviewModal.open({ modalData }).result.then((result) => {
			if (result == null) {
				return;
			}

			this.emitInstructionUpdate(this.editorInstructionsHtml(), this.MediaModel.model(result));
		});
	}

	public openRemoveMediaInstructionsModal (): void {
		this.emitInstructionUpdate(this.editorInstructionsHtml(), null);
	}

	public openLibrary (): void {
		this.ngxGoModalService.open(LibraryDialogComponent, false, {
			data: {
				filterType: [CONTENT_TYPES.MEDIA, CONTENT_TYPES.DOCUMENTS],
				mode: MODES.SELECT,
				previewable: false,
				collectionManagement: true,
				context: this.getLibraryDialogContext()
			}
		}).afterClosed().pipe(
			filter((selectedMedia) => this.mediaWasSelected(selectedMedia)),
			tap((res) => {
				this.emitInstructionUpdate(this.editorInstructionsHtml(), this.MediaModel.model(res));
			})
		).subscribe();
	}

	private setInstructionDraft (text: string): void {
		this.inlineTaskInstructionState?.setInstructionDraft(this.task(), text);
	}

	private remountInlineEditor (): void {
		this.clearInlineEditorRemountTimeout();
		this.inlineEditorMounted.set(false);
		this.inlineEditorRemountTimeout = setTimeout(() => {
			// Render the false branch first so the existing editor instance is destroyed before the next task mounts.
			this.cdr.detectChanges();
			this.inlineEditorMounted.set(true);
			this.inlineEditorRemountTimeout = null;
			this.cdr.markForCheck();
		});
	}

	private clearInlineEditorRemountTimeout (): void {
		if (this.inlineEditorRemountTimeout == null) {
			return;
		}

		clearTimeout(this.inlineEditorRemountTimeout);
		this.inlineEditorRemountTimeout = null;
	}

	private getInlineInstructionTaskKey (task: Task | undefined): InlineInstructionTaskKey | null {
		if (task == null) {
			return null;
		}

		return this.editorContext != null ? task.type : task.id;
	}

	private emitInstructionUpdate (text: string, media: MediaSchema | null): void {
		this.onEdit.emit({
			text,
			media
		});
	}

	private mediaWasSelected (media: unknown): media is MediaSchema {
		return (media as Partial<MediaSchema> | null)?.media_id != null;
	}

	private syncInstructionMedia (syncedMedia: Partial<MediaSchema> | null | undefined): void {
		const currentMedia = this.media();
		if (!this.inlineEditor() || currentMedia?.media_id == null || syncedMedia?.media_id == null) {
			return;
		}

		if (parseInt(String(currentMedia.media_id), 10) !== parseInt(String(syncedMedia.media_id), 10)) {
			return;
		}

		const modeledMedia = this.MediaModel.model({
			...currentMedia,
			...syncedMedia
		});
		const mediaCacheKey = modeledMedia.media_id == null ? modeledMedia : modeledMedia.media_id;
		this.modeledMediaCache.set(mediaCacheKey, modeledMedia);
		this.emitInstructionUpdate(this.editorInstructionsHtml(), modeledMedia);
	}

	private getTaskInstructionsDialogContextData (): Partial<Pick<TaskInstructionsDialogData, 'groupId' | 'context'>> {
		if (this.editorContext == null) {
			return {};
		}

		return {
			groupId: this.resolveEditorContextGroupId(),
			context: this.getLibraryDialogContext()
		};
	}

	private resolveEditorContextGroupId (): number | null {
		const groupId = (this.editorContext.group?.() as GroupContext | null)?.group_id;
		if (groupId != null) {
			return groupId;
		}

		return this.editorContext.activity()?.group_id ?? null;
	}

	private resolveGroupId (): number | null {
		if (this.editorContext != null) {
			return this.resolveEditorContextGroupId();
		}

		return null;
	}

	private getLibraryDialogContext (): LibraryDialogContext {
		return {
			group: this.editorContext?.group?.() ?? null,
			account: this.editorContext?.account?.() ?? null,
			org: this.editorContext?.org?.() ?? null,
			license: this.editorContext?.license?.() ?? null,
			orgSettings: this.editorContext?.orgSettings?.() ?? null
		};
	}
}
